import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PaymentCardType } from '@shinetools/pricing-plan-library';

import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import Loader from 'components/Loader';
import Stepper from 'components/Stepper';
import { StepOptions } from 'components/Stepper/types';
import { canOrderNewPhysicalCard } from 'features/PaymentCards/utils';

import {
  PhysicalCardOrderFlowContextProvider,
  usePhysicalCardOrderFlowContext,
} from './hooks/usePhysicalCardOrderFlowContext';
import { usePhysicalCardOrderFlowQuery } from './hooks/usePhysicalCardOrderFlowQuery';
import PhysicalCardOrderAddDeliveryInfo from './steps/PhysicalCardOrderAddDeliveryInfo';
import PhysicalCardOrderCardPin from './steps/PhysicalCardOrderCardPin';
import PhysicalCardOrderCardPinConfirmation from './steps/PhysicalCardOrderCardPinConfirmation';
import PhysicalCardOrderChooseDeliveryAddress from './steps/PhysicalCardOrderChooseDeliveryAddress';
import PhysicalCardOrderRecap from './steps/PhysicalCardOrderRecap';
import PhysicalCardOrderSuccess from './steps/PhysicalCardOrderSuccess';
import PhysicalCardOrderVerifyPersonalAddress from './steps/PhysicalCardOrderVerifyPersonalAddress';
import { mapQueryResultToFlowConfig } from './utils';

const PhysicalCardOrder: FC = () => {
  const { flowConfig } = usePhysicalCardOrderFlowContext();
  const { canSelectDeliveryAddress } = flowConfig;

  const steps: StepOptions[] = [];

  if (canSelectDeliveryAddress) {
    steps.push({
      key: 'select-delivery-address',
      render: (props) => <PhysicalCardOrderChooseDeliveryAddress {...props} />,
    });
  }
  steps.push(
    {
      key: 'verify-personal-address',
      render: (props) => <PhysicalCardOrderVerifyPersonalAddress {...props} />,
    },
    {
      key: 'add-delivery-info',
      render: (props) => <PhysicalCardOrderAddDeliveryInfo {...props} />,
    },
    {
      key: 'card-pin',
      render: (props) => <PhysicalCardOrderCardPin {...props} />,
    },
    {
      key: 'card-pin-confirmation',
      render: (props) => <PhysicalCardOrderCardPinConfirmation {...props} />,
    },
    {
      key: 'order-recap',
      render: (props) => <PhysicalCardOrderRecap {...props} />,
    },
    {
      key: 'order-success',
      render: (props) => <PhysicalCardOrderSuccess {...props} />,
    },
  );

  return <Stepper steps={steps} />;
};

const Container: FC = () => {
  const history = useHistory();
  const { data, error, loading } = usePhysicalCardOrderFlowQuery();
  const { plan } = useCurrentPricingPlan();

  const [hasCheckedOrderingAvailability, setHasCheckedOrderingAvailability] =
    useState(false);

  useEffect(() => {
    // We only want to execute this effect the first time the query is executed
    // and not after the data is updated when the card is ordered.
    if (!loading && data && !hasCheckedOrderingAvailability && plan) {
      const { creditCards } = data.viewer.company;
      const isOrderingAllowed = canOrderNewPhysicalCard({
        canOrderPremiumCard:
          plan.paymentCard.type === PaymentCardType.BusinessWorldDebitCard,
        cards: creditCards,
      });
      setHasCheckedOrderingAvailability(true);
      if (!isOrderingAllowed) {
        history.replace('/cards');
      }
    }
  }, [data, hasCheckedOrderingAvailability, history, loading, plan]);

  if (loading) {
    return <Loader />;
  }
  if (error || !data || !plan) {
    return <p>ERROR</p>;
  }

  const flowConfig = mapQueryResultToFlowConfig(data, plan);

  return (
    <PhysicalCardOrderFlowContextProvider value={flowConfig}>
      <PhysicalCardOrder />
    </PhysicalCardOrderFlowContextProvider>
  );
};

export default Container;
